import React, { useState, useEffect, useContext } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { AuthContext } from '../App';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import { ArrowLeft, Edit, Trash2, Mail, Plus, FileText, PoundSterling, Copy, Eye, Download } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../components/ui/dialog';

function WorktopDetail() {
  const { worktopId } = useParams();
  const navigate = useNavigate();
  const { token, API, user } = useContext(AuthContext);
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showEmailPreview, setShowEmailPreview] = useState(false);
  const [emailPreview, setEmailPreview] = useState('');
  const [sendingEmail, setSendingEmail] = useState(false);
  const [payments, setPayments] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [showPaymentDialog, setShowPaymentDialog] = useState(false);
  const [showInvoiceDialog, setShowInvoiceDialog] = useState(false);
  const [showInvoicePreview, setShowInvoicePreview] = useState(false);
  const [invoicePreviewContent, setInvoicePreviewContent] = useState('');
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [showCompletionPreview, setShowCompletionPreview] = useState(false);
  const [showNewInvoicePreview, setShowNewInvoicePreview] = useState(false);
  const [newInvoiceHtml, setNewInvoiceHtml] = useState('');
  const [sendingInvoice, setSendingInvoice] = useState(false);
  const [invoiceToEmail, setInvoiceToEmail] = useState('');
  const [showSectionQuotePreview, setShowSectionQuotePreview] = useState(false);
  const [sectionQuoteHtml, setSectionQuoteHtml] = useState('');
  const [sectionQuoteDetailed, setSectionQuoteDetailed] = useState(false);
  const [sectionQuoteToEmail, setSectionQuoteToEmail] = useState('');
  const [sendingSectionQuote, setSendingSectionQuote] = useState(false);
  const [completionEmailContent, setCompletionEmailContent] = useState('');
  const [sendingCompletionEmail, setSendingCompletionEmail] = useState(false);
  const [paymentForm, setPaymentForm] = useState({ amount: '', payment_method: 'Cash', notes: '' });
  const [invoiceForm, setInvoiceForm] = useState({ amount: '' });
  const [showDepositRequestDialog, setShowDepositRequestDialog] = useState(false);
  const [depositPercent, setDepositPercent] = useState('55');
  const [sendingDepositRequest, setSendingDepositRequest] = useState(false);
  
  // Quote options state
  const [showQuoteOptionDialog, setShowQuoteOptionDialog] = useState(false);
  const [editingQuoteOption, setEditingQuoteOption] = useState(null); // null for new, option obj for editing
  const [showQuoteOptionView, setShowQuoteOptionView] = useState(false);
  const [viewingQuoteOption, setViewingQuoteOption] = useState(null);
  const [savingQuoteOption, setSavingQuoteOption] = useState(false);
  const [quoteOptionForm, setQuoteOptionForm] = useState({
    stone_colour: '',
    stone_type: '',
    supplier: '',
    cost_per_sqm: '',
    notes: ''
  });

  useEffect(() => {
    fetchOrder();
    fetchPayments();
    fetchInvoices();
  }, [worktopId]);

  const fetchOrder = async () => {
    try {
      // Use alternative endpoint to bypass production cache
      const response = await axios.get(`${API}/worktop-details/${worktopId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setOrder(response.data);
    } catch (error) {
      toast.error('Failed to fetch order details');
      navigate('/worktops');
    } finally {
      setLoading(false);
    }
  };

  const fetchPayments = async () => {
    try {
      const response = await axios.get(`${API}/payments/order/${worktopId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPayments(response.data);
    } catch (error) {
      console.error('Failed to fetch payments');
    }
  };

  const fetchInvoices = async () => {
    try {
      const response = await axios.get(`${API}/invoices/order/${worktopId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setInvoices(response.data);
    } catch (error) {
      console.error('Failed to fetch invoices');
    }
  };

  const calculateBalance = () => {
    if (!order) return 0;
    const totalPaid = payments.reduce((sum, p) => sum + p.amount, 0);
    return order.total - totalPaid;
  };

  // Quote Options Functions
  const handleAddQuoteOption = async () => {
    if (!quoteOptionForm.stone_colour || !quoteOptionForm.cost_per_sqm) {
      toast.error('Please enter stone colour and cost per m²');
      return;
    }
    
    try {
      setSavingQuoteOption(true);
      await axios.post(`${API}/worktops/${worktopId}/quote-options`, {
        stone_colour: quoteOptionForm.stone_colour,
        stone_type: quoteOptionForm.stone_type || null,
        supplier: quoteOptionForm.supplier || null,
        cost_per_sqm: parseFloat(quoteOptionForm.cost_per_sqm) || 0,
        notes: quoteOptionForm.notes || null
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Quote option added - price calculated from order details');
      setShowQuoteOptionDialog(false);
      setQuoteOptionForm({ stone_colour: '', stone_type: '', supplier: '', cost_per_sqm: '', notes: '' });
      setEditingQuoteOption(null);
      fetchOrder();
    } catch (error) {
      const detail = error.response?.data?.detail;
      const msg = Array.isArray(detail) ? detail.map(e => e.msg || e.message || JSON.stringify(e)).join(', ') : (typeof detail === 'string' ? detail : 'Failed to add quote option');
      toast.error(msg);
    } finally {
      setSavingQuoteOption(false);
    }
  };

  const handleUpdateQuoteOption = async () => {
    if (!editingQuoteOption || !quoteOptionForm.stone_colour || !quoteOptionForm.cost_per_sqm) {
      toast.error('Please enter stone colour and cost per m²');
      return;
    }
    
    try {
      setSavingQuoteOption(true);
      await axios.put(`${API}/worktops/${worktopId}/quote-options/${editingQuoteOption.id}`, {
        stone_colour: quoteOptionForm.stone_colour,
        stone_type: quoteOptionForm.stone_type || null,
        supplier: quoteOptionForm.supplier || null,
        cost_per_sqm: parseFloat(quoteOptionForm.cost_per_sqm) || 0,
        notes: quoteOptionForm.notes || null
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Quote option updated');
      setShowQuoteOptionDialog(false);
      setQuoteOptionForm({ stone_colour: '', stone_type: '', supplier: '', cost_per_sqm: '', notes: '' });
      setEditingQuoteOption(null);
      fetchOrder();
    } catch (error) {
      const detail = error.response?.data?.detail;
      const msg = Array.isArray(detail) ? detail.map(e => e.msg || e.message || JSON.stringify(e)).join(', ') : (typeof detail === 'string' ? detail : 'Failed to update quote option');
      toast.error(msg);
    } finally {
      setSavingQuoteOption(false);
    }
  };

  const openEditQuoteOption = (option) => {
    setEditingQuoteOption(option);
    setQuoteOptionForm({
      stone_colour: option.stone_colour || '',
      stone_type: option.stone_type || '',
      supplier: option.supplier || '',
      cost_per_sqm: option.cost_per_sqm?.toString() || '',
      notes: option.notes || ''
    });
    setShowQuoteOptionDialog(true);
  };

  const openViewQuoteOption = (option) => {
    setViewingQuoteOption(option);
    setShowQuoteOptionView(true);
  };

  const handleDeleteQuoteOption = async (optionId) => {
    if (!window.confirm('Delete this quote option?')) return;
    try {
      await axios.delete(`${API}/worktops/${worktopId}/quote-options/${optionId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Quote option deleted');
      fetchOrder();
    } catch (error) {
      toast.error('Failed to delete quote option');
    }
  };

  const handleSelectQuoteOption = async (optionId) => {
    if (!window.confirm('Accept this quote option? This will update the order with this stone and pricing.')) return;
    try {
      await axios.post(`${API}/worktops/${worktopId}/quote-options/${optionId}/select`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Quote option accepted - order updated!');
      fetchOrder();
    } catch (error) {
      const detail = error.response?.data?.detail;
      const msg = Array.isArray(detail) ? detail.map(e => e.msg || e.message || JSON.stringify(e)).join(', ') : (typeof detail === 'string' ? detail : 'Failed to accept quote option');
      toast.error(msg);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Are you sure you want to delete this order?')) return;

    try {
      await axios.delete(`${API}/worktops/${worktopId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Order deleted successfully');
      navigate('/worktops');
    } catch (error) {
      toast.error('Failed to delete order');
    }
  };

  const handleDuplicate = async () => {
    if (!window.confirm('Create a copy of this quote?')) return;

    try {
      const response = await axios.post(`${API}/worktops/${worktopId}/duplicate`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Quote duplicated successfully');
      navigate(`/worktops/${response.data.id}`);
    } catch (error) {
      const detail = error.response?.data?.detail;
      const msg = Array.isArray(detail) ? detail.map(e => e.msg || e.message || JSON.stringify(e)).join(', ') : (typeof detail === 'string' ? detail : 'Failed to duplicate quote');
      toast.error(msg);
    }
  };

  const handleConvertToOrder = async () => {
    if (!window.confirm('Convert this quote to an order?')) return;

    try {
      await axios.patch(`${API}/worktops/${worktopId}/status`, 
        { status: 'accepted' },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Quote converted to order');
      fetchOrder();
    } catch (error) {
      toast.error('Failed to convert quote');
    }
  };

  const handleTakeDeposit = async () => {
    const suggested = order?.deposit_requested_amount ? String(order.deposit_requested_amount) : '';
    const depositAmount = window.prompt('Enter deposit amount (£):', suggested);
    if (!depositAmount) return;

    try {
      await axios.patch(`${API}/worktops/${worktopId}/deposit`, 
        { deposit: parseFloat(depositAmount) },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Deposit recorded');
      fetchOrder();
    } catch (error) {
      toast.error('Failed to record deposit');
    }
  };

  const handleRequestDeposit = async () => {
    const pct = parseFloat(depositPercent);
    if (!pct || pct <= 0 || pct > 100) {
      toast.error('Enter a percentage between 1 and 100');
      return;
    }
    setSendingDepositRequest(true);
    try {
      const res = await axios.post(`${API}/worktops/${worktopId}/request-deposit`,
        { percent: pct },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(res.data.message || 'Deposit request sent');
      setShowDepositRequestDialog(false);
      fetchOrder();
    } catch (error) {
      const detail = error.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Failed to send deposit request');
    } finally {
      setSendingDepositRequest(false);
    }
  };

  const handleMarkComplete = async () => {
    try {
      // Get completion email preview
      const response = await axios.get(`${API}/worktops/${worktopId}/completion-email-preview`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCompletionEmailContent(response.data.html || response.data);
      setShowCompletionPreview(true);
    } catch (error) {
      toast.error('Failed to load email preview');
    }
  };

  const handleConfirmComplete = async () => {
    setSendingCompletionEmail(true);
    try {
      // Send completion email
      await axios.post(`${API}/worktops/${worktopId}/send-completion-email`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Update status to completed
      await axios.patch(`${API}/worktops/${worktopId}/status`, 
        { status: 'completed' },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success('Order marked complete and client notified');
      setShowCompletionPreview(false);
      fetchOrder();
    } catch (error) {
      toast.error('Failed to complete order');
    } finally {
      setSendingCompletionEmail(false);
    }
  };

  const handleCompleteWithoutEmail = async () => {
    if (!window.confirm('Mark this order as complete WITHOUT sending an email to the customer?')) return;
    
    try {
      // Update status to completed without sending email
      await axios.patch(`${API}/worktops/${worktopId}/status`, 
        { status: 'completed' },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success('Order marked complete (no email sent)');
      fetchOrder();
    } catch (error) {
      toast.error('Failed to complete order');
    }
  };

  const handlePreviewEmail = async () => {
    try {
      const response = await axios.get(`${API}/worktops/${worktopId}/email-preview`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setEmailPreview(response.data.html);
      setShowEmailPreview(true);
    } catch (error) {
      toast.error('Failed to load email preview');
    }
  };

  const handleArchive = async () => {
    if (!window.confirm('Archive this completed order? It will be hidden from the main dashboard.')) return;
    
    try {
      await axios.patch(`${API}/worktops/${worktopId}/archive`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Order archived successfully');
      navigate('/worktops');
    } catch (error) {
      toast.error('Failed to archive order');
    }
  };

  const handleUnarchive = async () => {
    if (!window.confirm('Unarchive this order? It will be visible in the main dashboard again.')) return;
    
    try {
      await axios.patch(`${API}/worktops/${worktopId}/unarchive`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Order unarchived successfully');
      fetchOrder(); // Refresh the order data
    } catch (error) {
      toast.error('Failed to unarchive order');
    }
  };

  const handleAcceptQuote = async () => {
    if (!window.confirm('Accept this quote and proceed with the order?')) return;
    
    try {
      await axios.post(`${API}/trade/worktops/${worktopId}/accept-quote`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Quote accepted! We will be in touch soon.');
      fetchOrder();
    } catch (error) {
      toast.error('Failed to accept quote');
    }
  };

  const handleSendEmail = async (sendAsPdf = false) => {
    const confirmMessage = sendAsPdf 
      ? `Send quote as PDF to ${order.client_email}?`
      : `Send quote to ${order.client_email}?`;
    
    if (!window.confirm(confirmMessage)) return;

    setSendingEmail(true);
    try {
      await axios.post(`${API}/worktops/${worktopId}/send-quote?send_as_pdf=${sendAsPdf}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(sendAsPdf ? 'Quote sent as PDF successfully' : 'Quote sent successfully');
      setShowEmailPreview(false);
    } catch (error) {
      console.error('Email send error:', error.response?.data || error);
      const detail = error.response?.data?.detail;
      const errorDetail = Array.isArray(detail) ? detail.map(e => e.msg || e.message || JSON.stringify(e)).join(', ') : (typeof detail === 'string' ? detail : 'Failed to send email');
      toast.error(errorDetail);
      
      // If email failed, run diagnostic and show results
      try {
        const diagRes = await axios.get(`${API}/worktops/${worktopId}/email-diagnostic`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        console.log('Email Diagnostic:', diagRes.data);
        if (diagRes.data.issues && diagRes.data.issues.length > 0) {
          toast.error(`Issues found: ${diagRes.data.issues.join(', ')}`, { duration: 10000 });
          
          // If company_id is missing, offer to fix it
          if (diagRes.data.issues.some(i => i.includes('NO COMPANY_ID'))) {
            if (window.confirm('Order is missing company_id. Fix it now?')) {
              try {
                await axios.post(`${API}/worktops/${worktopId}/fix-company-id`, {}, {
                  headers: { Authorization: `Bearer ${token}` }
                });
                toast.success('Fixed! Try sending again.');
              } catch (fixErr) {
                const detail = fixErr.response?.data?.detail;
                const msg = Array.isArray(detail) ? detail.map(e => e.msg || e.message || JSON.stringify(e)).join(', ') : (typeof detail === 'string' ? detail : fixErr.message);
                toast.error('Could not fix: ' + msg);
              }
            }
          }
        }
      } catch (diagErr) {
        console.error('Diagnostic failed:', diagErr);
      }
    } finally {
      setSendingEmail(false);
    }
  };

  const handleDownloadPdf = async () => {
    try {
      const response = await axios.get(`${API}/worktops/${worktopId}/download-pdf`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Quote_${order.order_number}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('PDF downloaded');
    } catch (error) {
      const detail = error.response?.data?.detail;
      const msg = Array.isArray(detail) ? detail.map(e => e.msg || e.message || JSON.stringify(e)).join(', ') : (typeof detail === 'string' ? detail : 'Failed to download PDF');
      toast.error(msg);
    }
  };

  const handlePreviewSectionQuote = async (detailed = false) => {
    try {
      const response = await axios.get(`${API}/worktops/${worktopId}/section-quote-preview?detailed=${detailed}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSectionQuoteHtml(response.data.html);
      setSectionQuoteDetailed(detailed);
      setSectionQuoteToEmail(order?.client_email || '');
      setShowSectionQuotePreview(true);
    } catch (error) {
      const detail = error.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Failed to load quote preview');
    }
  };

  const handleSendSectionQuote = async () => {
    const destination = (sectionQuoteToEmail || '').trim();
    if (!destination || !destination.includes('@')) { toast.error('Enter a valid email address'); return; }
    setSendingSectionQuote(true);
    try {
      const res = await axios.post(
        `${API}/worktops/${worktopId}/send-section-quote?detailed=${sectionQuoteDetailed}`,
        { email: destination },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(res.data?.message || 'Quote emailed');
      setShowSectionQuotePreview(false);
    } catch (error) {
      const detail = error.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Failed to send quote');
    } finally {
      setSendingSectionQuote(false);
    }
  };

  const handleDownloadSectionPdf = async (detailed = false) => {
    try {
      const response = await axios.get(`${API}/worktops/${worktopId}/download-section-pdf?detailed=${detailed}`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      const prefix = detailed ? 'Detailed_Quote' : 'Quote';
      link.setAttribute('download', `${prefix}_${order.order_number}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success(detailed ? 'Detailed quote PDF downloaded' : 'Summary quote PDF downloaded');
    } catch (error) {
      const detail = error.response?.data?.detail;
      const msg = Array.isArray(detail) ? detail.map(e => e.msg || e.message || JSON.stringify(e)).join(', ') : (typeof detail === 'string' ? detail : 'Failed to download section PDF');
      toast.error(msg);
    }
  };

  const handlePreviewNewInvoice = async () => {
    try {
      const response = await axios.get(`${API}/worktops/${worktopId}/invoice-preview`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setNewInvoiceHtml(response.data.html);
      // Prefill the alt-email input with the client email each time the
      // preview opens; the user can override it before pressing Send.
      setInvoiceToEmail(order?.client_email || '');
      setShowNewInvoicePreview(true);
    } catch (error) {
      const detail = error.response?.data?.detail;
      const msg = Array.isArray(detail) ? detail.map(e => e.msg || e.message || JSON.stringify(e)).join(', ') : (typeof detail === 'string' ? detail : 'Failed to load invoice preview');
      toast.error(msg);
    }
  };

  const handleDownloadInvoicePdf = async () => {
    try {
      const response = await axios.get(`${API}/worktops/${worktopId}/download-invoice-pdf`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Invoice_${order.order_number}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('Invoice PDF downloaded');
    } catch (error) {
      const detail = error.response?.data?.detail;
      const msg = Array.isArray(detail) ? detail.map(e => e.msg || e.message || JSON.stringify(e)).join(', ') : (typeof detail === 'string' ? detail : 'Failed to download invoice PDF');
      toast.error(msg);
    }
  };

  const handleSendNewInvoice = async (sendAsPdf = true) => {
    const destination = (invoiceToEmail || '').trim();
    if (!destination) {
      toast.error('Please enter a recipient email address.');
      return;
    }
    // Loose sanity check — backend is the source of truth
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(destination)) {
      toast.error('That email address looks invalid.');
      return;
    }
    const isAlt = destination.toLowerCase() !== (order?.client_email || '').toLowerCase();
    const confirmMessage = sendAsPdf
      ? `Send invoice as PDF to ${destination}${isAlt ? ' (alternative email)' : ''}?`
      : `Send invoice to ${destination}${isAlt ? ' (alternative email)' : ''}?`;
    if (!window.confirm(confirmMessage)) return;

    setSendingInvoice(true);
    try {
      const params = new URLSearchParams({ send_as_pdf: String(sendAsPdf), to_email: destination });
      await axios.post(`${API}/worktops/${worktopId}/send-invoice?${params.toString()}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Invoice sent to ${destination}${sendAsPdf ? ' as PDF' : ''}`);
      setShowNewInvoicePreview(false);
    } catch (error) {
      const detail = error.response?.data?.detail;
      const msg = Array.isArray(detail) ? detail.map(e => e.msg || e.message || JSON.stringify(e)).join(', ') : (typeof detail === 'string' ? detail : 'Failed to send invoice');
      toast.error(msg);
    } finally {
      setSendingInvoice(false);
    }
  };

  const handleRecordPayment = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/payments`, {
        order_id: worktopId,
        amount: parseFloat(paymentForm.amount),
        payment_method: paymentForm.payment_method,
        notes: paymentForm.notes
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Payment recorded successfully');
      setShowPaymentDialog(false);
      setPaymentForm({ amount: '', payment_method: 'Cash', notes: '' });
      fetchPayments();
    } catch (error) {
      toast.error('Failed to record payment');
    }
  };

  const handleDeletePayment = async (paymentId) => {
    if (!window.confirm('Delete this payment?')) return;
    try {
      await axios.delete(`${API}/payments/${paymentId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Payment deleted');
      fetchPayments();
    } catch (error) {
      toast.error('Failed to delete payment');
    }
  };

  const handleCreateInvoice = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/invoices`, {
        order_id: worktopId,
        amount: parseFloat(invoiceForm.amount)
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Invoice created successfully');
      setShowInvoiceDialog(false);
      setInvoiceForm({ amount: '' });
      fetchInvoices();
    } catch (error) {
      toast.error('Failed to create invoice');
    }
  };

  const populateInvoiceAmount = () => {
    const balance = calculateBalance();
    setInvoiceForm({ amount: balance > 0 ? balance.toFixed(2) : '0.00' });
  };

  const handlePreviewInvoice = async (invoiceId) => {
    try {
      const response = await axios.get(`${API}/invoices/${invoiceId}/preview`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      // Extract the body HTML from the response
      setInvoicePreviewContent(response.data.body || response.data);
      setSelectedInvoice(invoices.find(inv => inv.id === invoiceId));
      setShowInvoicePreview(true);
    } catch (error) {
      toast.error('Failed to load invoice preview');
    }
  };

  const handleSendInvoice = async (invoiceId) => {
    if (!order.client_email) {
      toast.error('No email address for this client');
      return;
    }
    if (!window.confirm(`Send invoice to ${order.client_email}?`)) return;

    try {
      await axios.post(`${API}/invoices/${invoiceId}/send`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Invoice sent successfully');
      setShowInvoicePreview(false);
    } catch (error) {
      toast.error('Failed to send invoice');
    }
  };

  const handleDeleteInvoice = async (invoiceId) => {
    if (!window.confirm('Delete this invoice?')) return;
    try {
      await axios.delete(`${API}/invoices/${invoiceId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Invoice deleted');
      fetchInvoices();
    } catch (error) {
      toast.error('Failed to delete invoice');
    }
  };

  const handleGenerateJobSheet = async () => {
    try {
      const response = await axios.get(`${API}/worktops/${worktopId}/job-sheet`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'text'
      });
      const blob = new Blob([response.data], { type: 'text/html' });
      const url = window.URL.createObjectURL(blob);
      window.open(url, '_blank');
    } catch (error) {
      toast.error('Failed to generate job sheet');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-lg text-slate-600">Loading order...</div>
      </div>
    );
  }

  if (!order) return null;

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4">
      <div className="max-w-5xl mx-auto">
        {order.status === 'deposit-requested' && order.deposit_requested_amount && (
          <div className="mb-4 p-4 rounded-lg border border-violet-300 bg-violet-50 flex items-center justify-between flex-wrap gap-2" data-testid="deposit-requested-banner">
            <p className="text-sm text-violet-900">
              <strong>Deposit requested:</strong> {order.deposit_requested_percent}% = £{Number(order.deposit_requested_amount).toFixed(2)}
              {order.deposit_requested_at && ` on ${new Date(order.deposit_requested_at).toLocaleDateString()}`} — awaiting payment
            </p>
            <Button size="sm" className="bg-purple-600 hover:bg-purple-700" onClick={handleTakeDeposit}>Mark Deposit Paid</Button>
          </div>
        )}
        {order.status === 'deposit-taken' && order.deposit > 0 && (
          <div className="mb-4 p-4 rounded-lg border border-green-300 bg-green-50" data-testid="deposit-paid-banner">
            <p className="text-sm text-green-900">
              <strong>✓ Deposit paid:</strong> £{Number(order.deposit).toFixed(2)}
              {order.deposit_paid_at && ` on ${new Date(order.deposit_paid_at).toLocaleDateString()}`}
              {order.total > 0 && ` — balance remaining £${(Number(order.total) - Number(order.deposit)).toFixed(2)}`}
            </p>
          </div>
        )}
        <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
          <Button
            variant="ghost"
            onClick={() => navigate('/worktops')}
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Worktops
          </Button>
          <div className="flex flex-wrap gap-2">
            {/* Invoice - available for all orders */}
            <Button
              size="sm" 
              variant="outline"
              className="border-blue-300 text-blue-700 hover:bg-blue-50"
              onClick={handlePreviewNewInvoice}
            >
              <FileText className="w-4 h-4 mr-2" />
              Invoice
            </Button>
            {/* Quote PDF Download - available for all non-completed orders */}
            {order.status !== 'completed' && (
              <Button 
                size="sm" 
                variant="outline"
                className="border-purple-300 text-purple-700 hover:bg-purple-50"
                onClick={handleDownloadPdf}
              >
                <FileText className="w-4 h-4 mr-2" />
                Download Quote
              </Button>
            )}
            {/* Send Quote - for quote/draft statuses */}
            {(['quote', 'new_quote', 'draft', 'accepted', 'quoted', 'pending'].includes(order.status)) && (
              <Button 
                size="sm" 
                className="bg-blue-600 hover:bg-blue-700"
                onClick={handlePreviewEmail}
              >
                <Mail className="w-4 h-4 mr-2" />
                Preview & Send Quote
              </Button>
            )}
            {/* Section quote: preview, email or download — for multi-section orders */}
            {order.sections && order.sections.length > 0 && (
              <>
                <Button
                  size="sm"
                  variant="outline"
                  className="border-blue-300 text-blue-700 hover:bg-blue-50"
                  onClick={() => handlePreviewSectionQuote(false)}
                  data-testid="preview-summary-pdf"
                >
                  <Eye className="w-4 h-4 mr-2" />
                  Summary Quote
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  className="border-purple-300 text-purple-700 hover:bg-purple-50"
                  onClick={() => handlePreviewSectionQuote(true)}
                  data-testid="preview-detailed-pdf"
                >
                  <Eye className="w-4 h-4 mr-2" />
                  Detailed Quote
                </Button>
              </>
            )}
            {(['quote', 'new_quote', 'draft'].includes(order.status)) && (
              <Button 
                size="sm" 
                className="bg-green-600 hover:bg-green-700"
                onClick={handleConvertToOrder}
              >
                Convert to Order
              </Button>
            )}
            {['accepted', 'deposit-requested'].includes(order.status) && (
              <>
                <Button 
                  size="sm" 
                  className="bg-violet-600 hover:bg-violet-700"
                  onClick={() => setShowDepositRequestDialog(true)}
                  data-testid="request-deposit-btn"
                >
                  <PoundSterling className="w-4 h-4 mr-2" />
                  {order.status === 'deposit-requested' ? 'Resend Deposit Request' : 'Request Deposit'}
                </Button>
                <Button 
                  size="sm" 
                  className="bg-purple-600 hover:bg-purple-700"
                  onClick={handleTakeDeposit}
                  data-testid="take-deposit-btn"
                >
                  Take Deposit
                </Button>
              </>
            )}
            {order.status !== 'completed' && (
              <>
                <Button 
                  size="sm" 
                  className="bg-green-600 hover:bg-green-700"
                  onClick={handleMarkComplete}
                >
                  Mark Complete & Email
                </Button>
                <Button 
                  size="sm" 
                  variant="outline"
                  className="border-green-600 text-green-600 hover:bg-green-50"
                  onClick={handleCompleteWithoutEmail}
                >
                  Complete (No Email)
                </Button>
              </>
            )}
            {['admin', 'super_admin', 'company_admin'].includes(user?.role) && !order.is_archived && (
              <Button 
                size="sm" 
                className="bg-gray-600 hover:bg-gray-700"
                onClick={handleArchive}
                title="Archive this quote/order (can be unarchived later)"
                data-testid="archive-worktop-btn"
              >
                Archive {order.status === 'completed' ? 'Order' : 'Quote'}
              </Button>
            )}
            {['admin', 'super_admin', 'company_admin'].includes(user?.role) && order.is_archived && (
              <Button 
                size="sm" 
                className="bg-blue-600 hover:bg-blue-700"
                onClick={handleUnarchive}
                data-testid="unarchive-worktop-btn"
              >
                Unarchive {order.status === 'completed' ? 'Order' : 'Quote'}
              </Button>
            )}
            <Button 
              variant="outline" 
              size="sm"
              onClick={handleGenerateJobSheet}
            >
              <FileText className="w-4 h-4 mr-2" />
              Job Sheet
            </Button>
            {user?.role === 'trade' && ['quote', 'new_quote'].includes(order.status) && (
              <Button 
                size="sm" 
                className="bg-green-600 hover:bg-green-700"
                onClick={handleAcceptQuote}
              >
                Accept Quote
              </Button>
            )}
            {['admin', 'super_admin', 'company_admin'].includes(user?.role) && (
              <>
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={handleDuplicate}
                  className="text-blue-600 hover:text-blue-700"
                >
                  <Copy className="w-4 h-4 mr-2" />
                  Duplicate
                </Button>
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => navigate(`/worktops/${worktopId}/edit`)}
                >
                  <Edit className="w-4 h-4 mr-2" />
                  Edit
                </Button>
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={handleDelete}
                  className="text-red-600 hover:text-red-700"
                >
                  <Trash2 className="w-4 h-4 mr-2" />
                  Delete
                </Button>
              </>
            )}
          </div>
        </div>

        <Card className="mb-6">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="text-2xl">Order #{order.order_number}</CardTitle>
                <p className="text-sm text-gray-500 mt-1">{order.order_type}</p>
              </div>
              <div className="text-right">
                <div className="text-3xl font-bold text-gray-900">£{order.total.toFixed(2)}</div>
                <div className="text-sm text-gray-500">inc. VAT</div>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-sm text-gray-600">Client Name</p>
                <p className="font-semibold">{order.client_name}</p>
              </div>
              {order.client_email && (
                <div>
                  <p className="text-sm text-gray-600">Email</p>
                  <p className="font-semibold">{order.client_email}</p>
                </div>
              )}
              {order.client_phone && (
                <div>
                  <p className="text-sm text-gray-600">Phone</p>
                  <p className="font-semibold">{order.client_phone}</p>
                </div>
              )}
              {order.client_address && (
                <div>
                  <p className="text-sm text-gray-600">Address</p>
                  <p className="font-semibold">{order.client_address}</p>
                </div>
              )}
              <div>
                <p className="text-sm text-gray-600">Status</p>
                <p className="font-semibold capitalize">{order.status.replace('-', ' ')}</p>
                {order.invoiced_waiting_payment && (
                  <span
                    className="inline-flex items-center gap-1 px-2 py-1 rounded-md text-xs font-semibold bg-amber-100 text-amber-800 border border-amber-300 mt-1"
                    data-testid="invoiced-waiting-payment-badge"
                    title={`Invoice sent ${order.invoiced_waiting_payment_at ? 'on ' + new Date(order.invoiced_waiting_payment_at).toLocaleDateString() : ''}. Auto-clears when payment is recorded.`}
                  >
                    Invoiced — Waiting Payment
                  </span>
                )}
              </div>
              <div>
                <p className="text-sm text-gray-600">Created</p>
                <p className="font-semibold">{new Date(order.created_at).toLocaleDateString()}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Quote Options - For comparing different stone colours/prices.
            Available throughout the quote stage (draft / new_quote / quote / quote-sent)
            so users can build alternatives whether they're saving a draft or have
            already shared the first version with the client. Hidden once the
            client has accepted or moved beyond quoting. */}
        {['draft', 'new_quote', 'quote', 'quote-sent'].includes(order.status) && (
          <Card className="mb-6 border-2 border-blue-200 bg-blue-50">
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="text-blue-800">Quote Options</CardTitle>
                <Button 
                  size="sm" 
                  onClick={() => {
                    setEditingQuoteOption(null);
                    setQuoteOptionForm({ stone_colour: '', stone_type: '', supplier: '', cost_per_sqm: '', notes: '' });
                    setShowQuoteOptionDialog(true);
                  }}
                  className="bg-blue-600 hover:bg-blue-700"
                  data-testid="add-quote-option-btn"
                >
                  <Plus className="w-4 h-4 mr-1" />
                  Add Option
                </Button>
              </div>
              <p className="text-sm text-blue-600">Compare different stone colours and prices side-by-side. The total is auto-calculated from the order details (multi-section, edges, extras and services all included) using the new £/m² for the stone. Use this when the client hasn't picked a colour yet — they see all options on one detailed quote. Click an option to accept it.</p>
            </CardHeader>
            <CardContent>
              {(!order.quote_options || order.quote_options.length === 0) ? (
                <p className="text-gray-500 text-center py-4">No quote options added yet. Add options to compare different stone colours/prices.</p>
              ) : (
                <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                  {order.quote_options.map((option, idx) => (
                    <div 
                      key={option.id} 
                      className={`p-4 rounded-lg border-2 cursor-pointer transition-all hover:shadow-md ${option.is_selected ? 'border-green-500 bg-green-50' : 'border-gray-200 bg-white hover:border-blue-300'}`}
                      onClick={() => openViewQuoteOption(option)}
                      data-testid={`quote-option-${idx}`}
                    >
                      <div className="flex justify-between items-start mb-2">
                        <div>
                          <p className="font-bold text-lg">{option.stone_colour}</p>
                          {option.stone_type && <p className="text-sm text-gray-500">{option.stone_type}</p>}
                          {option.supplier && <p className="text-xs text-gray-400">Supplier: {option.supplier}</p>}
                        </div>
                        <span className="text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded">Option {idx + 1}</span>
                      </div>
                      
                      <div className="space-y-1 text-sm mb-3">
                        <div className="border-t pt-2 mt-2">
                          <p className="text-gray-600">Subtotal: £{option.subtotal?.toFixed(2) || '0.00'}</p>
                          <p className="text-gray-600">VAT ({order.vat_rate || 20}%): £{option.vat_amount?.toFixed(2) || '0.00'}</p>
                          <p className="font-bold text-2xl text-gray-900 mt-1">£{option.total?.toFixed(2) || '0.00'}</p>
                        </div>
                      </div>
                      
                      {option.notes && (
                        <p className="text-xs text-gray-500 italic mb-3 truncate">{option.notes}</p>
                      )}
                      
                      <div className="flex gap-2" onClick={(e) => e.stopPropagation()}>
                        <Button 
                          size="sm" 
                          className="flex-1 bg-green-600 hover:bg-green-700"
                          onClick={() => handleSelectQuoteOption(option.id)}
                          data-testid={`accept-option-${idx}`}
                        >
                          Accept
                        </Button>
                        <Button 
                          size="sm" 
                          variant="outline"
                          onClick={() => openEditQuoteOption(option)}
                          data-testid={`edit-option-${idx}`}
                        >
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button 
                          size="sm" 
                          variant="outline"
                          className="text-red-600 border-red-300 hover:bg-red-50"
                          onClick={() => handleDeleteQuoteOption(option.id)}
                          data-testid={`delete-option-${idx}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Selected Quote Option Display (for accepted orders) */}
        {order.selected_option_id && order.quote_options?.length > 0 && (
          <Card className="mb-6 border-2 border-green-200 bg-green-50">
            <CardHeader>
              <CardTitle className="text-green-800">Selected Quote Option</CardTitle>
            </CardHeader>
            <CardContent>
              {order.quote_options.filter(o => o.id === order.selected_option_id).map(option => (
                <div key={option.id} className="flex justify-between items-center">
                  <div>
                    <p className="font-bold text-lg">{option.stone_colour}</p>
                    {option.stone_type && <p className="text-sm text-gray-600">{option.stone_type}</p>}
                  </div>
                  <p className="font-bold text-2xl text-green-700">£{option.total?.toFixed(2)}</p>
                </div>
              ))}
            </CardContent>
          </Card>
        )}

        {/* Surfacing/Stonemasonry Details */}
        {(order.order_type === 'Surfacing' || order.order_type === 'Stonemasonry') ? (
          <>
            {/* Job Description */}
            {order.job_description && (
              <Card className="mb-6 border-l-4 border-amber-500">
                <CardHeader>
                  <CardTitle className="text-amber-900">Job Description</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="whitespace-pre-wrap text-sm">{order.job_description}</p>
                </CardContent>
              </Card>
            )}

            {/* Surfaces - for Surfacing type */}
            {order.order_type === 'Surfacing' && order.surfaces?.length > 0 && (
              <Card className="mb-6 border-l-4 border-cyan-500">
                <CardHeader>
                  <CardTitle className="text-cyan-900">Surfaces</CardTitle>
                </CardHeader>
                <CardContent>
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b-2 border-gray-300">
                        <th className="text-left py-2 px-2">#</th>
                        <th className="text-left py-2 px-2">Surface</th>
                        <th className="text-left py-2 px-2">Length (mm)</th>
                        <th className="text-left py-2 px-2">Width (mm)</th>
                        <th className="text-left py-2 px-2">Work Type</th>
                      </tr>
                    </thead>
                    <tbody>
                      {order.surfaces.map((s, i) => (
                        <tr key={i} className="border-b border-gray-200">
                          <td className="py-2 px-2 text-gray-600">{i + 1}</td>
                          <td className="py-2 px-2 font-medium">{s.name || '-'}</td>
                          <td className="py-2 px-2">{s.length_mm || '-'}</td>
                          <td className="py-2 px-2">{s.width_mm || '-'}</td>
                          <td className="py-2 px-2 capitalize">{(s.work_type || '-').replace(/-/g, ' ')}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </CardContent>
              </Card>
            )}

            {/* Stone Blocks - for Stonemasonry type */}
            {order.order_type === 'Stonemasonry' && order.stone_blocks?.length > 0 && (
              <Card className="mb-6 border-l-4 border-stone-500">
                <CardHeader>
                  <CardTitle className="text-stone-900">Stone Blocks & Pieces</CardTitle>
                </CardHeader>
                <CardContent>
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b-2 border-gray-300">
                        <th className="text-left py-2 px-2">#</th>
                        <th className="text-left py-2 px-2">Description</th>
                        <th className="text-left py-2 px-2">L (mm)</th>
                        <th className="text-left py-2 px-2">W (mm)</th>
                        <th className="text-left py-2 px-2">D (mm)</th>
                        <th className="text-left py-2 px-2">Qty</th>
                      </tr>
                    </thead>
                    <tbody>
                      {order.stone_blocks.map((b, i) => (
                        <tr key={i} className="border-b border-gray-200">
                          <td className="py-2 px-2 text-gray-600">{i + 1}</td>
                          <td className="py-2 px-2 font-medium">{b.description || '-'}</td>
                          <td className="py-2 px-2">{b.length_mm || '-'}</td>
                          <td className="py-2 px-2">{b.width_mm || '-'}</td>
                          <td className="py-2 px-2">{b.depth_mm || '-'}</td>
                          <td className="py-2 px-2">{b.quantity || 1}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </CardContent>
              </Card>
            )}

            {/* Line Items */}
            {order.line_items?.length > 0 && (
              <Card className="mb-6 border-l-4 border-green-500">
                <CardHeader>
                  <CardTitle className="text-green-900">Quoted Items</CardTitle>
                </CardHeader>
                <CardContent>
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b-2 border-gray-300">
                        <th className="text-left py-2 px-2">#</th>
                        <th className="text-left py-2 px-2">Description</th>
                        <th className="text-center py-2 px-2">Qty</th>
                        <th className="text-right py-2 px-2">Unit Price</th>
                        <th className="text-right py-2 px-2">Total</th>
                      </tr>
                    </thead>
                    <tbody>
                      {order.line_items.map((item, i) => {
                        const qty = parseInt(item.qty) || 1;
                        const price = parseFloat(item.price || 0);
                        const total = price * qty;
                        return (
                          <tr key={i} className="border-b border-gray-200">
                            <td className="py-2 px-2 text-gray-600">{i + 1}</td>
                            <td className="py-2 px-2">{item.description}</td>
                            <td className="py-2 px-2 text-center">{qty}</td>
                            <td className="py-2 px-2 text-right">£{price.toFixed(2)}</td>
                            <td className="py-2 px-2 text-right font-medium">£{total.toFixed(2)}</td>
                          </tr>
                        );
                      })}
                      <tr className="border-t-2 border-gray-300 font-semibold bg-gray-50">
                        <td colSpan="4" className="py-2 px-2 text-right">Subtotal:</td>
                        <td className="py-2 px-2 text-right">
                          £{order.line_items.reduce((sum, item) => sum + (parseFloat(item.price || 0) * (parseInt(item.qty) || 1)), 0).toFixed(2)}
                        </td>
                      </tr>
                    </tbody>
                  </table>
                  
                  {/* Price Per Unit Summary */}
                  {order.number_of_units > 0 && (
                    <div className="mt-4 p-4 bg-blue-50 rounded-lg border border-blue-200">
                      <div className="flex justify-between items-center">
                        <div>
                          <span className="text-blue-800 font-medium">Number of Units:</span>
                          <span className="ml-2 text-blue-900 font-bold">{order.number_of_units}</span>
                        </div>
                        <div>
                          <span className="text-blue-800 font-medium">Price Per Unit:</span>
                          <span className="ml-2 text-blue-900 font-bold text-xl">
                            £{(order.subtotal / order.number_of_units).toFixed(2)}
                          </span>
                        </div>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            )}
          </>
        ) : (
          <>
        {/* Stone Details / Sections */}
        {order.multi_section && order.sections?.length > 0 ? (
          /* Multi-Section Display */
          <div className="space-y-4 mb-6">
            {order.sections.map((section, sIdx) => (
              <Card key={section.id || sIdx} className="border-l-4 border-l-blue-500">
                <CardHeader className="pb-2">
                  <CardTitle className="text-base flex items-center gap-2">
                    <span className="bg-blue-600 text-white text-xs font-bold px-2 py-0.5 rounded">S{sIdx + 1}</span>
                    {section.name}
                  </CardTitle>
                  <p className="text-sm text-gray-500">{section.supplier} — {section.colour_name} (£{section.pm2_cost}/m²)</p>
                </CardHeader>
                <CardContent>
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b-2 border-gray-300">
                        <th className="text-left py-1 px-2">#</th>
                        <th className="text-left py-1 px-2">Name</th>
                        <th className="text-left py-1 px-2">Width</th>
                        <th className="text-left py-1 px-2">Depth</th>
                        <th className="text-left py-1 px-2">Edge</th>
                        <th className="text-left py-1 px-2">m²</th>
                        <th className="text-left py-1 px-2">Qty</th>
                        <th className="text-right py-1 px-2">Cost</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(section.pieces || []).map((p, pIdx) => {
                        const area = ((p.width_mm * p.height_mm) / 1000000);
                        const qty = p.quantity || 1;
                        const pieceCost = area * (section.pm2_cost || 0) * 3.1 * qty;
                        return (
                          <tr key={pIdx} className="border-b border-gray-100">
                            <td className="py-1 px-2 text-gray-500">{pIdx + 1}</td>
                            <td className="py-1 px-2 font-medium">{p.name || '-'}</td>
                            <td className="py-1 px-2">{p.width_mm}mm</td>
                            <td className="py-1 px-2">{p.height_mm}mm</td>
                            <td className="py-1 px-2 text-xs">{p.edge_profile || '-'}</td>
                            <td className="py-1 px-2">{area.toFixed(2)}</td>
                            <td className="py-1 px-2">{qty}</td>
                            <td className="py-1 px-2 text-right font-medium">£{pieceCost.toFixed(2)}</td>
                          </tr>
                        );
                      })}
                      {section.cnc_hours > 0 && (
                        <tr className="border-b border-gray-100 bg-gray-50">
                          <td colSpan="7" className="py-1 px-2 text-xs text-gray-600">CNC Work ({section.cnc_hours}hrs × £{section.cnc_rate || 65}/hr)</td>
                          <td className="py-1 px-2 text-right font-medium">£{(section.cnc_hours * (section.cnc_rate || 65)).toFixed(2)}</td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                  {section.ve_options?.length > 0 && (
                    <div className="mt-3 p-2 bg-amber-50 rounded border border-amber-200">
                      <p className="text-xs font-semibold text-amber-700 mb-1">VE Options:</p>
                      {section.ve_options.map((ve, vIdx) => (
                        <p key={vIdx} className="text-xs text-amber-800">{ve.supplier} — {ve.colour_name} (£{ve.pm2_cost}/m²)</p>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        ) : (
          <>
        {/* Stone Details (Single Material) */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle>Stone Details</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-3 gap-4 text-sm">
              <div>
                <span className="text-gray-600">Supplier:</span>
                <p className="font-medium">{order.pieces[0]?.supplier || 'N/A'}</p>
              </div>
              <div>
                <span className="text-gray-600">Colour:</span>
                <p className="font-medium">{order.pieces[0]?.colour_name || 'N/A'}</p>
              </div>
              <div>
                <span className="text-gray-600">Cost/m²:</span>
                <p className="font-medium">£{order.pieces[0]?.pm2_cost || 0}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Pieces */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle>Stone Pieces</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b-2 border-gray-300">
                    <th className="text-left py-2 px-2">#</th>
                    <th className="text-left py-2 px-2">Name</th>
                    <th className="text-left py-2 px-2">Width (mm)</th>
                    <th className="text-left py-2 px-2">Height (mm)</th>
                    <th className="text-left py-2 px-2">Thickness</th>
                    <th className="text-left py-2 px-2">Area (m²)</th>
                    <th className="text-left py-2 px-2">Qty</th>
                  </tr>
                </thead>
                <tbody>
                  {order.pieces.map((piece, index) => (
                    <tr key={index} className="border-b border-gray-200">
                      <td className="py-2 px-2 text-gray-600">{index + 1}</td>
                      <td className="py-2 px-2 font-medium">{piece.name || '-'}</td>
                      <td className="py-2 px-2">{piece.width_mm}</td>
                      <td className="py-2 px-2">{piece.height_mm}</td>
                      <td className="py-2 px-2">{piece.thickness}</td>
                      <td className="py-2 px-2 font-medium">{((piece.width_mm * piece.height_mm) / 1000000).toFixed(2)}</td>
                      <td className="py-2 px-2">{piece.quantity || 1}</td>
                    </tr>
                  ))}
                  <tr className="border-t-2 border-gray-300 font-semibold">
                    <td colSpan="6" className="py-2 px-2 text-right">Total Area:</td>
                    <td className="py-2 px-2">
                      {order.pieces.reduce((sum, p) => sum + (p.width_mm * p.height_mm / 1000000), 0).toFixed(2)} m²
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
          </>
        )}

        {/* Extras & Services */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle>Extras & Services</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2 text-sm">
              {order.edge_profile && (
                <div>✓ Edge Profile: {order.edge_profile} ({(order.total_linear_metres || 0).toFixed(2)}m)</div>
              )}
              {order.undermount_sink && <div>✓ Undermount Sink</div>}
              {order.overmount_sink && <div>✓ Overmount Sink</div>}
              {order.hob_cutout && <div>✓ Hob Cut-out</div>}
              {order.custom_fabrication_cost > 0 && <div>✓ Custom Fabrication (£{order.custom_fabrication_cost.toFixed(2)})</div>}
              {order.socket_cutout && <div>✓ Socket Cut-out {order.socket_cutout_qty > 1 ? `(x${order.socket_cutout_qty})` : ''}</div>}
              {order.popup_socket && <div>✓ Pop-up Socket</div>}
              {order.tap_hole && <div>✓ Tap Hole</div>}
              {order.radius_corner && <div>✓ Radius Corner</div>}
              {order.large_rounded_corner && <div>✓ Large Rounded Corner</div>}
              {order.drainer_grooves && (
                <div>
                  ✓ Drainer Grooves
                  {order.drainer_grooves_side && (
                    <span className="text-slate-600"> - {order.drainer_grooves_side.charAt(0).toUpperCase() + order.drainer_grooves_side.slice(1)} side of sink</span>
                  )}
                  {order.drainer_grooves_length && (
                    <span className="text-slate-600"> ({order.drainer_grooves_length}mm)</span>
                  )}
                </div>
              )}
              {order.recessed_drainer && <div>✓ Recessed Drainer</div>}
              {order.include_templating && <div>✓ Templating Service</div>}
              {order.include_fitting && <div>✓ Fitting Service</div>}
            </div>
          </CardContent>
        </Card>
          </>
        )}

        {/* Attached Files */}
        {order.attached_files && order.attached_files.length > 0 && (
          <Card className="mb-6">
            <CardHeader>
              <CardTitle>Attached Drawings / Documents</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {order.attached_files.map((file, index) => (
                  <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded border">
                    <div className="flex items-center gap-3">
                      <FileText className="w-5 h-5 text-gray-600" />
                      <span className="text-sm">{file.original_name}</span>
                    </div>
                    <a 
                      href={`${API}${file.url}`}
                      download={file.original_name}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-amber-600 hover:text-amber-700 text-sm font-medium"
                    >
                      Download
                    </a>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Pricing Breakdown */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle>Pricing</CardTitle>
          </CardHeader>
          <CardContent>
            {/* Pricing breakdown — IMPORTANT: `order.subtotal` is stored as the
                POST-discount value, so to show the discount correctly we have to
                gross it back up: pre = subtotal / (1 - rate). Previously we
                showed `subtotal × rate%` which produced a misleading lower
                discount amount (e.g. £249.69 instead of the real £277.44 on a
                £2496.96 post-discount subtotal at 10%). */}
            {(() => {
              const rate = Number(order.trade_discount || 0);
              const postDiscountSub = Number(order.subtotal || 0);
              const hasDiscount = rate > 0 && rate < 100;
              const preDiscountSub = hasDiscount ? postDiscountSub / (1 - rate / 100) : postDiscountSub;
              const discountAmount = preDiscountSub - postDiscountSub;
              return (
                <div className="space-y-2">
                  {hasDiscount && (
                    <div className="flex justify-between text-slate-500">
                      <span>Subtotal (before discount)</span>
                      <span>£{preDiscountSub.toFixed(2)}</span>
                    </div>
                  )}
                  {hasDiscount && (
                    <div className="flex justify-between text-green-600">
                      <span>Trade Discount ({rate}%)</span>
                      <span>-£{discountAmount.toFixed(2)}</span>
                    </div>
                  )}
                  <div className="flex justify-between">
                    <span>{hasDiscount ? 'Subtotal (after discount)' : 'Subtotal'}</span>
                    <span>£{postDiscountSub.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>VAT ({order.vat_rate}%)</span>
                    <span>£{Number(order.vat_amount || 0).toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between font-bold text-lg pt-2 border-t">
                    <span>Total</span>
                    <span>£{Number(order.total || 0).toFixed(2)}</span>
                  </div>
                  {payments.length > 0 && (
                    <>
                      <div className="flex justify-between text-sm text-gray-600 pt-2 border-t">
                        <span>Total Paid</span>
                        <span>£{payments.reduce((sum, p) => sum + p.amount, 0).toFixed(2)}</span>
                      </div>
                      <div className="flex justify-between font-bold text-lg" style={{ color: calculateBalance() > 0 ? '#dc2626' : '#16a34a' }}>
                        <span>Balance Due</span>
                        <span>{calculateBalance() <= 0 ? 'PAID ✓' : `£${calculateBalance().toFixed(2)}`}</span>
                      </div>
                    </>
                  )}
                </div>
              );
            })()}
          </CardContent>
        </Card>

        {/* Payments Section */}
        <Card className="mb-6">
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle>Payments</CardTitle>
              <div className="flex gap-2">
                <Button 
                  size="sm" 
                  onClick={() => setShowPaymentDialog(true)}
                  className="bg-green-600 hover:bg-green-700"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Record Payment
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {payments.length === 0 ? (
              <p className="text-gray-500 text-sm">No payments recorded yet</p>
            ) : (
              <div className="space-y-3">
                {payments.map((payment) => (
                  <div key={payment.id} className="flex items-center justify-between p-3 bg-gray-50 rounded border">
                    <div>
                      <div className="font-semibold">£{payment.amount.toFixed(2)}</div>
                      <div className="text-sm text-gray-600">{payment.payment_method}</div>
                      {payment.notes && <div className="text-xs text-gray-500 mt-1">{payment.notes}</div>}
                      <div className="text-xs text-gray-400 mt-1">
                        {new Date(payment.created_at).toLocaleString()}
                      </div>
                    </div>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => handleDeletePayment(payment.id)}
                      className="text-red-600 hover:text-red-700"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Legacy per-order Invoices card intentionally removed — the
            "Preview Invoice" button at the top of the page is the single
            source of truth for invoice generation, and reflects live
            payments/deposits from the payments collection. */}

        {order.notes && (
          <Card className="mt-6">
            <CardHeader>
              <CardTitle>Notes</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-gray-700 whitespace-pre-wrap font-mono" style={{ 
                lineHeight: '1.8'
              }}>{order.notes.split('***').map((part, idx) => 
                idx === 1 ? <strong key={idx} className="text-lg text-blue-700">***{part}***</strong> : part
              )}</p>
            </CardContent>
          </Card>
        )}

        {/* Email Preview Dialog */}
        <Dialog open={showEmailPreview} onOpenChange={setShowEmailPreview}>
          <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Mail className="w-5 h-5" />
                Email Preview - Quote for {order.client_name}
              </DialogTitle>
            </DialogHeader>
            <div className="mt-4">
              <div className="mb-4 p-3 bg-gray-50 rounded border">
                <p className="text-sm"><strong>To:</strong> {order.client_email}</p>
                <p className="text-sm"><strong>Subject:</strong> Your {order.order_type === 'Fireplace/Hearth' ? 'Hearth' : 'Worktop'} Quote - Order #{order.order_number}</p>
              </div>
              <div 
                className="border rounded p-4 bg-white"
                dangerouslySetInnerHTML={{ __html: emailPreview }}
              />
              <div className="flex gap-3 justify-end mt-6">
                <Button
                  variant="outline"
                  onClick={() => setShowEmailPreview(false)}
                >
                  Cancel
                </Button>
                <Button
                  variant="outline"
                  onClick={handleDownloadPdf}
                  className="border-purple-300 text-purple-700 hover:bg-purple-50"
                >
                  <FileText className="w-4 h-4 mr-2" />
                  Download PDF
                </Button>
                <Button
                  onClick={() => handleSendEmail(true)}
                  disabled={sendingEmail}
                  variant="outline"
                  className="border-green-300 text-green-700 hover:bg-green-50"
                >
                  {sendingEmail ? 'Sending...' : 'Send as PDF'}
                </Button>
                <Button
                  onClick={() => handleSendEmail(false)}
                  disabled={sendingEmail}
                  className="bg-blue-600 hover:bg-blue-700"
                >
                  {sendingEmail ? 'Sending...' : 'Send as Email'}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Record Payment Dialog */}
        <Dialog open={showPaymentDialog} onOpenChange={setShowPaymentDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Record Payment</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleRecordPayment}>
              <div className="space-y-4 py-4">
                <div>
                  <Label htmlFor="amount">Amount (£)</Label>
                  <Input
                    id="amount"
                    type="number"
                    step="0.01"
                    value={paymentForm.amount}
                    onChange={(e) => setPaymentForm({ ...paymentForm, amount: e.target.value })}
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="payment_method">Payment Method</Label>
                  <select
                    id="payment_method"
                    value={paymentForm.payment_method}
                    onChange={(e) => setPaymentForm({ ...paymentForm, payment_method: e.target.value })}
                    className="w-full border rounded px-3 py-2"
                  >
                    <option>Cash</option>
                    <option>Card</option>
                    <option>Bank Transfer</option>
                    <option>Cheque</option>
                  </select>
                </div>
                <div>
                  <Label htmlFor="notes">Notes (Optional)</Label>
                  <Textarea
                    id="notes"
                    value={paymentForm.notes}
                    onChange={(e) => setPaymentForm({ ...paymentForm, notes: e.target.value })}
                    rows={3}
                  />
                </div>
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setShowPaymentDialog(false)}>
                  Cancel
                </Button>
                <Button type="submit" className="bg-green-600 hover:bg-green-700">
                  Record Payment
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>

        {/* Create Invoice Dialog removed — legacy per-order invoices section
            is no longer used. See the "Preview Invoice" modal below which
            renders directly from live order + payments data. */}

        {/* Invoice Preview Dialog */}
        <Dialog open={showInvoicePreview} onOpenChange={setShowInvoicePreview}>
          <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <FileText className="w-5 h-5" />
                Invoice Preview - {order.client_name}
              </DialogTitle>
            </DialogHeader>
            <div className="mt-4">
              <div 
                className="border rounded p-4 bg-white"
                dangerouslySetInnerHTML={{ __html: invoicePreviewContent }}
              />
              <div className="flex gap-3 justify-end mt-6">
                <Button
                  variant="outline"
                  onClick={() => setShowInvoicePreview(false)}
                >
                  Close
                </Button>
                {selectedInvoice && (
                  <Button
                    onClick={() => handleSendInvoice(selectedInvoice.id)}
                    className="bg-blue-600 hover:bg-blue-700"
                  >
                    <Mail className="w-4 h-4 mr-2" />
                    Send to Client
                  </Button>
                )}
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Completion Email Preview Dialog */}
        <Dialog open={showCompletionPreview} onOpenChange={setShowCompletionPreview}>
          <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Mail className="w-5 h-5" />
                Completion Notification - {order.client_name}
              </DialogTitle>
            </DialogHeader>
            <div className="mt-4">
              <div className="mb-4 p-3 bg-gray-50 rounded border">
                <p className="text-sm"><strong>To:</strong> {order.client_email}</p>
                <p className="text-sm"><strong>Subject:</strong> Your Worktop Order #{order.order_number} - Fabrication Complete</p>
              </div>
              <div 
                className="border rounded p-4 bg-white"
                dangerouslySetInnerHTML={{ __html: completionEmailContent }}
              />
              <div className="flex gap-3 justify-end mt-6">
                <Button
                  variant="outline"
                  onClick={() => setShowCompletionPreview(false)}
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleConfirmComplete}
                  disabled={sendingCompletionEmail}
                  className="bg-green-600 hover:bg-green-700"
                >
                  {sendingCompletionEmail ? 'Processing...' : 'Send Email & Mark Complete'}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Quote Option Dialog - Add/Edit */}
        <Dialog open={showQuoteOptionDialog} onOpenChange={(open) => {
          if (!open) {
            setEditingQuoteOption(null);
            setQuoteOptionForm({ stone_colour: '', stone_type: '', supplier: '', cost_per_sqm: '', notes: '' });
          }
          setShowQuoteOptionDialog(open);
        }}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>{editingQuoteOption ? 'Edit Quote Option' : 'Add Quote Option'}</DialogTitle>
              <DialogDescription>
                {editingQuoteOption 
                  ? 'Update the stone details. The total will be recalculated automatically.'
                  : 'Enter the stone details. The total price will be calculated automatically using the order specifications (pieces, extras, services, etc.)'}
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div>
                <Label>Stone Colour / Name *</Label>
                <Input
                  value={quoteOptionForm.stone_colour}
                  onChange={(e) => setQuoteOptionForm({...quoteOptionForm, stone_colour: e.target.value})}
                  placeholder="e.g., Black Galaxy, Kashmir White"
                  data-testid="quote-option-colour"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Stone Type</Label>
                  <Input
                    value={quoteOptionForm.stone_type}
                    onChange={(e) => setQuoteOptionForm({...quoteOptionForm, stone_type: e.target.value})}
                    placeholder="e.g., Granite, Quartz"
                    data-testid="quote-option-type"
                  />
                </div>
                <div>
                  <Label>Supplier</Label>
                  <Input
                    value={quoteOptionForm.supplier}
                    onChange={(e) => setQuoteOptionForm({...quoteOptionForm, supplier: e.target.value})}
                    placeholder="e.g., Supplier name"
                    data-testid="quote-option-supplier"
                  />
                </div>
              </div>
              <div>
                <Label>Code (£) *</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={quoteOptionForm.cost_per_sqm}
                  onChange={(e) => setQuoteOptionForm({...quoteOptionForm, cost_per_sqm: e.target.value})}
                  placeholder="e.g., 150.00"
                  data-testid="quote-option-cost"
                />
              </div>
              <div>
                <Label>Notes</Label>
                <Textarea
                  value={quoteOptionForm.notes}
                  onChange={(e) => setQuoteOptionForm({...quoteOptionForm, notes: e.target.value})}
                  placeholder="Optional notes about this option (e.g., lead time, special features)"
                  rows={2}
                  data-testid="quote-option-notes"
                />
              </div>
              
              {/* Info box about auto-calculation */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm">
                <p className="text-blue-800 font-medium mb-1">Automatic Price Calculation</p>
                <p className="text-blue-600">
                  The subtotal, VAT, and total will be calculated based on:
                </p>
                <ul className="text-blue-600 text-xs mt-1 ml-4 list-disc">
                  <li>Stone area × cost/m² × retail multiplier</li>
                  <li>Edge profiles, cutouts, and extras</li>
                  <li>Templating, fitting, and delivery</li>
                  <li>Any discounts applied</li>
                </ul>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => {
                setShowQuoteOptionDialog(false);
                setEditingQuoteOption(null);
                setQuoteOptionForm({ stone_colour: '', stone_type: '', supplier: '', cost_per_sqm: '', notes: '' });
              }}>
                Cancel
              </Button>
              <Button 
                onClick={editingQuoteOption ? handleUpdateQuoteOption : handleAddQuoteOption}
                disabled={!quoteOptionForm.stone_colour || !quoteOptionForm.cost_per_sqm || savingQuoteOption}
                className="bg-blue-600 hover:bg-blue-700"
                data-testid="save-quote-option-btn"
              >
                {savingQuoteOption ? 'Saving...' : (editingQuoteOption ? 'Update Option' : 'Add Option')}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Quote Option View Dialog */}
        <Dialog open={showQuoteOptionView} onOpenChange={setShowQuoteOptionView}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>Quote Option Details</DialogTitle>
            </DialogHeader>
            {viewingQuoteOption && (
              <div className="space-y-4 py-4">
                <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg p-4">
                  <h3 className="text-xl font-bold text-gray-900">{viewingQuoteOption.stone_colour}</h3>
                  {viewingQuoteOption.stone_type && (
                    <p className="text-gray-600">{viewingQuoteOption.stone_type}</p>
                  )}
                  {viewingQuoteOption.supplier && (
                    <p className="text-sm text-gray-500">Supplier: {viewingQuoteOption.supplier}</p>
                  )}
                </div>
                
                <div className="grid grid-cols-3 gap-4">
                  <div className="bg-gray-50 rounded-lg p-3">
                    <p className="text-xs text-gray-500">Subtotal</p>
                    <p className="text-lg font-semibold">£{viewingQuoteOption.subtotal?.toFixed(2) || '0.00'}</p>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-3">
                    <p className="text-xs text-gray-500">VAT ({order?.vat_rate || 20}%)</p>
                    <p className="text-lg font-semibold">£{viewingQuoteOption.vat_amount?.toFixed(2) || '0.00'}</p>
                  </div>
                  <div className="bg-green-50 rounded-lg p-3 border-2 border-green-200">
                    <p className="text-xs text-green-600">Total</p>
                    <p className="text-2xl font-bold text-green-700">£{viewingQuoteOption.total?.toFixed(2) || '0.00'}</p>
                  </div>
                </div>
                
                {viewingQuoteOption.notes && (
                  <div className="bg-amber-50 rounded-lg p-3">
                    <p className="text-xs text-amber-600 mb-1">Notes</p>
                    <p className="text-sm text-gray-700">{viewingQuoteOption.notes}</p>
                  </div>
                )}
                
                <div className="flex gap-2 pt-4 border-t">
                  <Button 
                    className="flex-1 bg-green-600 hover:bg-green-700"
                    onClick={() => {
                      setShowQuoteOptionView(false);
                      handleSelectQuoteOption(viewingQuoteOption.id);
                    }}
                  >
                    Accept This Quote
                  </Button>
                  <Button 
                    variant="outline"
                    onClick={() => {
                      setShowQuoteOptionView(false);
                      openEditQuoteOption(viewingQuoteOption);
                    }}
                  >
                    <Edit className="w-4 h-4 mr-1" />
                    Edit
                  </Button>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Invoice Preview Modal */}
        {showNewInvoicePreview && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] flex flex-col">
              <div className="p-4 border-b flex items-center justify-between bg-blue-50">
                <h2 className="text-xl font-bold text-blue-800">Invoice Preview</h2>
                <Button variant="ghost" size="sm" onClick={() => setShowNewInvoicePreview(false)}>
                  ✕
                </Button>
              </div>
              <div className="flex-1 overflow-auto p-4">
                <div 
                  className="border rounded-lg bg-white"
                  dangerouslySetInnerHTML={{ __html: newInvoiceHtml }}
                />
              </div>
              <div className="p-4 border-t bg-gray-50">
                <div className="mb-3">
                  <Label htmlFor="invoice-to-email" className="text-xs text-slate-600">
                    Send to email
                  </Label>
                  <Input
                    id="invoice-to-email"
                    type="email"
                    value={invoiceToEmail}
                    onChange={(e) => setInvoiceToEmail(e.target.value)}
                    placeholder={order?.client_email || 'client@example.com'}
                    className="mt-1"
                    data-testid="invoice-alt-email-input"
                  />
                  {order?.client_email && invoiceToEmail && invoiceToEmail.toLowerCase() !== order.client_email.toLowerCase() && (
                    <p className="text-xs text-amber-700 mt-1">
                      Sending to alternative email (not {order.client_email}).
                    </p>
                  )}
                </div>
                <div className="flex gap-3 justify-end">
                  <Button
                    variant="outline"
                    onClick={() => setShowNewInvoicePreview(false)}
                  >
                    Cancel
                  </Button>
                  <Button
                    variant="outline"
                    onClick={handleDownloadInvoicePdf}
                    className="border-purple-300 text-purple-700 hover:bg-purple-50"
                  >
                    <FileText className="w-4 h-4 mr-2" />
                    Download PDF
                  </Button>
                  <Button
                    onClick={() => handleSendNewInvoice(true)}
                    disabled={sendingInvoice}
                    className="bg-blue-700 hover:bg-blue-800"
                    data-testid="send-invoice-btn"
                  >
                    {sendingInvoice ? 'Sending...' : 'Send Invoice (PDF)'}
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Section Quote Preview + Email/Download dialog */}
        {showSectionQuotePreview && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] flex flex-col">
              <div className="p-4 border-b flex items-center justify-between bg-blue-50">
                <h2 className="text-xl font-bold text-blue-800">
                  {sectionQuoteDetailed ? 'Detailed Quote Preview' : 'Summary Quote Preview'}
                </h2>
                <Button variant="ghost" size="sm" onClick={() => setShowSectionQuotePreview(false)}>✕</Button>
              </div>
              <div className="flex-1 overflow-auto p-4">
                <div className="border rounded-lg bg-white" dangerouslySetInnerHTML={{ __html: sectionQuoteHtml }} />
              </div>
              <div className="p-4 border-t bg-gray-50">
                <div className="mb-3">
                  <Label htmlFor="section-quote-email" className="text-xs text-slate-600">Send to email</Label>
                  <Input
                    id="section-quote-email"
                    type="email"
                    value={sectionQuoteToEmail}
                    onChange={(e) => setSectionQuoteToEmail(e.target.value)}
                    placeholder={order?.client_email || 'client@example.com'}
                    className="mt-1"
                    data-testid="section-quote-email-input"
                  />
                  {order?.client_email && sectionQuoteToEmail && sectionQuoteToEmail.toLowerCase() !== order.client_email.toLowerCase() && (
                    <p className="text-xs text-amber-700 mt-1">Sending to alternative email (not {order.client_email}).</p>
                  )}
                </div>
                <div className="flex gap-3 justify-end flex-wrap">
                  <Button variant="outline" onClick={() => setShowSectionQuotePreview(false)}>Close</Button>
                  <Button
                    variant="outline"
                    onClick={() => handleDownloadSectionPdf(sectionQuoteDetailed)}
                    className="border-purple-300 text-purple-700 hover:bg-purple-50"
                    data-testid="section-quote-download-btn"
                  >
                    <Download className="w-4 h-4 mr-2" /> Download PDF
                  </Button>
                  <Button
                    onClick={handleSendSectionQuote}
                    disabled={sendingSectionQuote}
                    className="bg-blue-700 hover:bg-blue-800"
                    data-testid="section-quote-send-btn"
                  >
                    <Mail className="w-4 h-4 mr-2" />
                    {sendingSectionQuote ? 'Sending...' : 'Email Quote (PDF)'}
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Deposit Request Dialog */}
        <Dialog open={showDepositRequestDialog} onOpenChange={setShowDepositRequestDialog}>
          <DialogContent className="max-w-md" data-testid="deposit-request-dialog">
            <DialogHeader>
              <DialogTitle>Request Deposit</DialogTitle>
              <DialogDescription>
                Email {order?.client_name || 'the client'} a deposit request with your bank details.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label htmlFor="deposit-percent">Deposit percentage (%)</Label>
                <Input
                  id="deposit-percent"
                  type="number"
                  min="1"
                  max="100"
                  step="0.5"
                  value={depositPercent}
                  onChange={(e) => setDepositPercent(e.target.value)}
                  data-testid="deposit-percent-input"
                />
              </div>
              <div className="p-4 rounded-lg bg-violet-50 border border-violet-200 space-y-1 text-sm">
                <div className="flex justify-between"><span>Order total:</span><span className="font-medium">£{Number(order?.total || 0).toFixed(2)}</span></div>
                <div className="flex justify-between text-violet-800 font-semibold text-base">
                  <span>Deposit ({depositPercent || 0}%):</span>
                  <span data-testid="deposit-calculated-amount">£{((Number(order?.total || 0) * (parseFloat(depositPercent) || 0)) / 100).toFixed(2)}</span>
                </div>
                <div className="flex justify-between text-slate-500"><span>Balance after deposit:</span><span>£{(Number(order?.total || 0) - (Number(order?.total || 0) * (parseFloat(depositPercent) || 0)) / 100).toFixed(2)}</span></div>
              </div>
              <p className="text-xs text-slate-500">Sends to {order?.client_email || 'no email on file'} and marks the order as "Deposit Requested".</p>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowDepositRequestDialog(false)}>Cancel</Button>
              <Button
                onClick={handleRequestDeposit}
                disabled={sendingDepositRequest || !order?.client_email}
                className="bg-violet-600 hover:bg-violet-700"
                data-testid="send-deposit-request-btn"
              >
                {sendingDepositRequest ? 'Sending…' : 'Send Deposit Request'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
}

export default WorktopDetail;